/**
 * Pricing. All rates live here — change a number and every screen follows.
 *
 * Longer trips get a lower day rate: the sidekick's fixed effort (learning
 * your taste, building the plan) is amortised over more days.
 */

export interface PriceTier {
  /** Applies when trip length is at most this many days. */
  maxDays: number;
  /** USD per day for the sidekick. */
  rate: number;
}

/** Ordered shortest to longest; the first tier that fits wins. */
export const TIERS: PriceTier[] = [
  { maxDays: 3, rate: 129 },
  { maxDays: 7, rate: 109 },
  { maxDays: Infinity, rate: 89 },
];

/**
 * Per-day cost of a private car and driver, added on top of the sidekick.
 *
 * Families need one — three tier-one cities are large, and metro transfers
 * with a small child and a stroller are the part of a trip people remember
 * badly. Quoted as a separate line so it is obvious what the money buys.
 */
export const CAR_DAY_RATE = 150;

/** Refundable, and not charged in the app — the booking is an intent. */
export const DEPOSIT = 99;

/** Trip lengths offered on the landing screen, aligned to the tiers. */
export const TRIP_LENGTHS = [3, 7, 14] as const;

export interface Quote {
  days: number;
  dayRate: number;
  sidekickTotal: number;
  withCar: boolean;
  carDayRate: number;
  carTotal: number;
  total: number;
  deposit: number;
}

export function dayRateFor(days: number): number {
  return (TIERS.find((t) => days <= t.maxDays) ?? TIERS[TIERS.length - 1]!).rate;
}

export function quote(days: number, opts: { withCar?: boolean } = {}): Quote {
  const d = Math.max(1, Math.round(days));
  const dayRate = dayRateFor(d);
  const sidekickTotal = dayRate * d;
  const withCar = Boolean(opts.withCar);
  const carTotal = withCar ? CAR_DAY_RATE * d : 0;
  return {
    days: d,
    dayRate,
    sidekickTotal,
    withCar,
    carDayRate: CAR_DAY_RATE,
    carTotal,
    total: sidekickTotal + carTotal,
    deposit: DEPOSIT,
  };
}
